import React from 'react';
import { Insights, AutoFixHigh, MenuBook, Spellcheck, Campaign } from '@mui/icons-material';

export interface ResearcherCardData {
  icon: React.ReactNode;
  shortTitle: string;
  description: string;
  prompt: string;
}

export const researcherCards: ResearcherCardData[] = [
  {
    icon: <Insights fontSize="small" />,
    shortTitle: 'Market Trends',
    description: 'Researcher agent analyses the market trends and identify growing and potential (sunrise) industries.',
    prompt: `Analyze current market trends and identify growing and potential industries, also known as sunrise industries, with a specific focus on the industrial fastening and assembly technology sector.

Please consider the following aspects in your analysis:
- Economic Indicators: Examine key economic indicators such as GDP growth rates, employment rates, and consumer spending patterns.
- Technological Advancements: Identify industries benefiting from recent technological innovations and breakthroughs, particularly in fastening and assembly technology.
- Consumer Behavior: Analyze shifts in consumer preferences and behaviors that are driving demand in sectors related to industrial fastening and assembly.
- Regulatory Environment: Consider the impact of government policies, regulations, and incentives on the industrial fastening and assembly technology industry.
- Global Trends: Take into account global economic trends, trade dynamics, and geopolitical factors affecting the industry.
- Sustainability and Environmental Impact: Evaluate industries that are focusing on sustainability and have a positive environmental impact, especially in the context of industrial fastening and assembly.
- Investment and Funding: Look at trends in venture capital, private equity, and other forms of investment that indicate growing interest in the industrial fastening and assembly technology sector.

Provide a comprehensive report that includes:
- A report title.
- A summary of the current market landscape.
- Identification of the top 5 growing and potential industries within the context of industrial fastening and assembly technology.
- Detailed analysis of each identified industry, including key drivers, challenges, and future outlook.
- Recommendations for businesses and investors looking to enter or expand in these industries.`,
  },
  {
  icon: <MenuBook fontSize="small" />,
  shortTitle: 'Competitor Landscape',
  description: 'Researcher agent maps competitors, their positioning, and strategic moves in industrial fastening and assembly technology.',
  prompt: `Analyze the current competitor landscape in the industrial fastening and assembly technology sector, identifying key players, their market positioning, and strategic direction.

Please consider the following aspects in your analysis:
- Market Share Distribution: Identify major global and regional competitors and estimate their relative market influence.
- Product Portfolio Comparison: Compare competitor offerings by category, innovation level, and value proposition.
- Pricing and Commercial Strategy: Evaluate pricing models, channel strategies, and bundled service approaches.
- Geographic Presence: Assess competitor strength by region, including expansion and localization efforts.
- Strategic Initiatives: Review M&A activity, partnerships, digital transformation programs, and new product launches.
- Customer Perception: Analyze available signals on brand trust, quality reputation, and service differentiation.
- Capability Gaps: Identify areas where competitors appear weak or underinvested.

Provide a comprehensive report that includes:
- A report title.
- A summary of the current competitive landscape.
- Identification of the top 5 most relevant competitors for Bossard.
- Detailed analysis of each competitor, including strengths, weaknesses, and strategic intent.
- Recommendations on where and how to differentiate effectively.`,
},
  {
  icon: <Campaign fontSize="small" />,
  shortTitle: 'Customer Segments',
  description: 'Researcher agent identifies high-potential customer segments and demand drivers across target industries.',
  prompt: `Analyze customer segments with the highest growth and profitability potential for industrial fastening and assembly technology solutions.

Please consider the following aspects in your analysis:
- Industry Segment Prioritization: Assess segments such as automotive, electronics, aerospace, medical devices, and renewable energy.
- Buying Criteria: Identify key purchasing factors including total cost of ownership, reliability, speed, and technical support.
- Decision-Making Units: Map who influences and approves purchasing decisions in each segment.
- Demand Drivers: Analyze macro and operational trends driving fastening and assembly demand.
- Pain Points: Identify recurring customer challenges in production, quality, supply continuity, and sustainability goals.
- Adoption Readiness: Evaluate each segment’s openness to digital services, smart assembly, and automation.
- Revenue Potential: Estimate relative opportunity size and expected growth trajectory by segment.

Provide a comprehensive report that includes:
- A report title.
- A summary of segment-level market demand.
- Identification of the top 5 customer segments to prioritize.
- Detailed analysis of each segment, including needs, barriers, and expected value.
- Recommendations for segment-specific positioning and engagement.`,
},
  {
  icon: <AutoFixHigh fontSize="small" />,
  shortTitle: 'Innovation Signals',
  description: 'Researcher agent tracks emerging technologies and innovation signals shaping the future of fastening and assembly.',
  prompt: `Analyze emerging technology and innovation signals that are likely to reshape the industrial fastening and assembly technology sector over the next 3 to 5 years.

Please consider the following aspects in your analysis:
- R&D and Patent Activity: Identify areas with accelerating innovation and technical investment.
- Materials Innovation: Assess developments in lightweight, high-performance, and sustainable materials.
- Smart Manufacturing Integration: Evaluate trends in IoT-enabled fastening, sensorized quality control, and digital traceability.
- Automation and Robotics: Analyze the role of cobots, automated assembly cells, and AI-assisted process optimization.
- Digital Service Layer: Review innovation in digital twins, predictive maintenance, and software-enabled value creation.
- Startup and Venture Ecosystem: Identify emerging companies and innovation clusters relevant to the sector.
- Commercial Maturity: Classify trends by maturity stage, adoption barriers, and expected timeline to scale.

Provide a comprehensive report that includes:
- A report title.
- A summary of the innovation landscape.
- Identification of the top 5 technologies or innovation themes to watch.
- Detailed analysis of each theme, including impact potential, risks, and adoption timeline.
- Recommendations for where to invest, partner, or monitor closely.`,
},
  {
  icon: <Spellcheck fontSize="small" />,
  shortTitle: 'Strategy Brief',
  description: 'Researcher agent converts market evidence into a strategic brief with clear priorities and decision options.',
  prompt: `Develop a strategic research brief for leadership on growth opportunities in industrial fastening and assembly technology, based on current market evidence.

Please consider the following aspects in your analysis:
- Opportunity Mapping: Identify and compare near-term and mid-term growth opportunities.
- Strategic Fit: Evaluate alignment with current capabilities, partnerships, and regional presence.
- Risk Assessment: Assess execution risks, market uncertainty, and dependency constraints.
- Investment Requirements: Estimate capability, talent, technology, and channel investments required.
- Scenario Analysis: Build best-case, base-case, and downside scenarios for selected opportunities.
- Priority Sequencing: Recommend the order of initiatives based on impact, feasibility, and speed.
- Measurement Framework: Define KPIs and milestones for tracking execution and outcomes.

Provide a comprehensive report that includes:
- A report title.
- A summary of strategic context and decision needs.
- Identification of the top 5 strategic opportunities to evaluate.
- Detailed analysis of each opportunity, including business case assumptions and risk profile.
- Recommendations with a phased action roadmap for leadership.`,
},
];

export default researcherCards;
